// ============================================================================
// APP CHECK CONFIGURATION
//
// Firebase App Check reduces unauthorized client abuse: a valid token attests
// the call came from a genuine, unmodified ReferralGate web app. It is the OUTER
// transport-edge boundary — Auth, tenant isolation, RBAC and the PHI matrix are
// still enforced server-side regardless.
//
// SETUP INSTRUCTIONS:
// 1. Go to Firebase Console > App Check
// 2. Register your app with reCAPTCHA v3 (web)
// 3. Get your reCAPTCHA v3 site key (public; safe to ship in the client bundle)
// 4. Add VITE_RECAPTCHA_SITE_KEY to your .env file
// 5. Deploy with server enforcement OFF (APP_CHECK_ENFORCE=false), confirm
//    clients are sending tokens in the Console, THEN enable enforcement.
//
// ROLLOUT POSTURE: until VITE_RECAPTCHA_SITE_KEY is set this is a safe no-op —
// the app runs normally with App Check simply absent. The decision logic lives
// in the pure, unit-tested ./appCheckPolicy.js.
// ============================================================================

import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { resolveAppCheckBootstrap } from './appCheckPolicy.js';

// Idempotency guard: both bootstrap paths (auth.firebase.js + store.firebase.js)
// call this immediately after initializeApp(), but App Check must be initialized
// at most once per app — a second initializeAppCheck() would throw. We decide
// once and cache the result (instance or null).
let appCheckAttempted = false;
let appCheckInstance = null;

/**
 * Initialize Firebase App Check for the given app (idempotent, safe-by-default).
 *
 * @param {import('firebase/app').FirebaseApp} app - Firebase app instance
 * @returns {import('firebase/app-check').AppCheck|null} instance, or null when
 *          App Check is not configured / failed (never throws to the caller).
 */
export const initializeAppCheckForApp = (app) => {
  if (appCheckAttempted) return appCheckInstance; // already decided once
  appCheckAttempted = true;

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const { shouldInitialize, debugToken } = resolveAppCheckBootstrap({
    siteKey,
    isDev: import.meta.env.DEV,
    debugToken: import.meta.env.VITE_APPCHECK_DEBUG_TOKEN,
  });

  if (!shouldInitialize) {
    console.warn(
      'App Check not initialized: VITE_RECAPTCHA_SITE_KEY not set. ' +
      'For production, configure App Check to prevent unauthorized access.'
    );
    return null;
  }

  try {
    // Dev-only: register a debug token so local/test clients pass attestation.
    if (debugToken !== null) {
      // @ts-ignore - global Firebase reads for dev App Check debug tokens
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }

    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });

    console.log('App Check initialized successfully');
    return appCheckInstance;
  } catch (err) {
    console.error('Error initializing App Check:', err);
    return null;
  }
};

export default initializeAppCheckForApp;
